if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();
        this.cachedMap = new Map();
        this.requestVersion = 0;
        this.focusElement = this.input;
        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() { return this.querySelector('input[type="search"]'); }
      get resetButton() { return this.querySelector('button[type="reset"]'); }
      get typewriter() { return this.input.previousElementSibling; }
      get resultsElement() { return this.querySelector('.search__results'); }

      get supportsPredictions() {
        // Shopify rejects the Arabic buyer locale even when its feature flag is true.
        if (this.predictionsUnavailable || document.documentElement.lang.split('-')[0] === 'ar') return false;
        try {
          return JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch !== false;
        } catch { return true; }
      }

      showSubmit(show = true) {
        const button = this.querySelector('[data-search-submit]');
        if (!button) return;
        button.hidden = !show;
        button.style.display = show ? '' : 'none';
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) this.typewriter.setAttribute('hidden', '');
      }

      resetPredictions() {
        this.abortController?.abort();
        this.requestVersion += 1;
        this.removeAttribute('loading');
        this.removeAttribute('results');
        this.removeAttribute('aria-busy');
        if (this.resultsElement) this.resultsElement.replaceChildren();
        document.body.classList.remove('predictive-search-open');
        this.showSubmit();
      }

      onFocus() {
        if (!this.closest('.collection')) return;
        if (!this.getQuery() || !this.supportsPredictions) {
          this.resetPredictions();
          return;
        }
        document.body.classList.add('predictive-search-open');
        this.renderSection(this.buildUrl().toString());
      }

      clear(event = null) {
        if (event) event.preventDefault();
        this.resetPredictions();
        this.input.value = '';
        this.input.focus();
      }

      getQuery() { return this.input.value.trim(); }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (!this.getQuery()) { this.clear(); return; }
        this.hideTypewriter();
        if (!this.supportsPredictions) { this.resetPredictions(); return; }
        this.renderSection(this.buildUrl().toString());
      }

      async renderSection(url) {
        this.abortController?.abort();
        const version = ++this.requestVersion;
        const query = this.getQuery();
        const controller = new AbortController();
        this.abortController = controller;
        this.removeAttribute('results');
        this.setAttribute('loading', '');
        this.setAttribute('aria-busy', 'true');
        this.showSubmit();
        try {
          let responseText = this.cachedMap.get(url);
          if (responseText === undefined) {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) {
              if (response.status === 417 && version === this.requestVersion) this.predictionsUnavailable = true;
              throw new Error(`Predictive search unavailable (${response.status})`);
            }
            responseText = await response.text();
          }
          if (version !== this.requestVersion || query !== this.getQuery()) return;
          const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
          const result = new DOMParser().parseFromString(responseText, 'text/html').getElementById(id);
          if (!result || !this.resultsElement) throw new Error('Predictive search response is incomplete');
          this.resultsElement.innerHTML = result.innerHTML;
          this.cachedMap.set(url, responseText);
          this.setAttribute('results', '');
          // A successful empty-result response has no See all results button.
          this.showSubmit(!this.resultsElement.querySelector('button[type="submit"]'));
        } catch (error) {
          if (version !== this.requestVersion) return;
          this.removeAttribute('results');
          if (this.resultsElement) this.resultsElement.replaceChildren();
          document.body.classList.remove('predictive-search-open');
          this.showSubmit();
        } finally {
          if (version === this.requestVersion) {
            this.removeAttribute('loading');
            this.removeAttribute('aria-busy');
          }
        }
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
